// The Brief: a dashboard opens with what changed and what drove it, written out, one line per headline number.
// The sentences come from Explain (rules over the same numbers as the tiles, not a model), so they're always right,
// and each line opens the inspector on that number.
import type { DTile, XExplanation } from "../api";

export type BriefItem = { tile: DTile; ex: XExplanation };

/** "Biggest driver: customer Great Lakes Trailer, −1.1 pts (its own margin % went …), partly offset by …" →
 *  "Mostly customer Great Lakes Trailer, −1.1 pts, partly offset by …": the inspector has the detail. */
const short = (s: string) => s.replace(/^Biggest driver: /, "Mostly ").replace(/ \([^()]*(?:\([^()]*\)[^()]*)*\)/g, "");

/** "Net Sales fell $28.9K (−8.6%) to $309K against the prior year." with the change set in bold and colored. */
function Sentence({ text, tone }: { text: string; tone: string }) {
  const cut = text.search(/ Biggest driver: /);
  const head = (cut < 0 ? text : text.slice(0, cut)).replace(/ against the prior (year|period)\./, "."), rest = cut < 0 ? "" : text.slice(cut + 1);
  const m = head.match(/^(.*? (?:grew|rose|fell) )(.+?)( to .*)$/);
  return (
    <span className="brief-s">
      {m ? <>{m[1]}<b className={tone}>{m[2]}</b>{m[3]}</> : head}
      {rest && <span className="brief-why"> {short(rest)}</span>}
    </span>
  );
}

export function Brief({ items, loading, range, onOpen }: { items: BriefItem[]; loading: boolean; range: string | null; onOpen: (tileId: string) => void }) {
  const cmp = items.find((x) => x.ex.compare)?.ex.compare;
  if (!items.length && !loading) return null;
  return (
    <section className="brief" aria-label="What changed">
      <div className="brief-tag">
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 3.5h11M2.5 8h11M2.5 12.5h7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
        <span>The Brief</span>
        {range && <em>{range}</em>}
        {cmp && <em>vs {cmp === "py" ? "prior year" : "prior period"}</em>}
      </div>
      <div className="brief-lines">
        {!items.length ? [0, 1, 2].map((i) => <span key={i} className="sk" style={{ width: `${[82, 64, 72][i]}%` }} />) : items.map(({ tile, ex }) => {
          const good = tile.view?.good !== "down";
          const tone = ex.change === null || Math.abs(ex.change) < 1e-9 ? "flat" : (ex.change > 0) === good ? "good" : "bad";
          return (
            <button key={tile.id} className="brief-l" onClick={() => onOpen(tile.id)} title={`Why: ${tile.title}`}>
              <i className={`brief-dot ${tone}`} aria-hidden="true" />
              <Sentence text={ex.headline} tone={tone} />
              <span className="brief-go" aria-hidden="true">Why →</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
